import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { 
  Box,
  Container, 
  Paper, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { 
  Activity as ActivityIcon,
  Home as HomeIcon, 
  Users as UsersIcon, 
  UserMinus as UserMinusIcon,
  PieChart as ChartIcon,
  BarChart as BarChartIcon,
  Map as MapIcon,
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  ThumbsUp,
  ThumbsDown,
  TrendingUp
} from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const GrafikRegisterUsulan = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('bar');
  const [dataAll, setDataAll] = useState({
    totalData: 0,
    totalStatusProses: 0,
    totalStatusDisetujui: 0,
    totalStatusDitolak: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_URL}/getgrafikregisterusulan`, { 
          withCredentials: true 
        });

        if (response.data && response.data.code === 200) {
          const data = response.data.data[0]; // Ambil data pertama dari array
          setDataAll({
            totalData: data.totalData || 0,
            totalStatusProses: data.totalStatusProses || 0,
            totalStatusDisetujui: data.totalStatusDisetujui || 0,
            totalStatusDitolak: data.totalStatusDitolak || 0
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  // Data untuk chart overall
  const overallBarData = {
    labels: ["Status Usulan"],
    datasets: [
      {
        label: "Proses",
        data: [dataAll.totalStatusProses],
        backgroundColor: "#f59e0b",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#d97706',
      },
      {
        label: "Disetujui",
        data: [dataAll.totalStatusDisetujui],
        backgroundColor: "#22c55e",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#16a34a',
      },
      {
        label: "Ditolak",
        data: [dataAll.totalStatusDitolak],
        backgroundColor: "#ef4444",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#dc2626',
      },
    ],
  };

  const overallPieData = {
    labels: ["Proses", "Disetujui", "Ditolak"],
    datasets: [
      {
        data: [dataAll.totalStatusProses, dataAll.totalStatusDisetujui, dataAll.totalStatusDitolak],
        backgroundColor: ["#f59e0b", "#22c55e", "#ef4444"],
        borderColor: ["#fff", "#fff", "#fff"],
        borderWidth: 2,
        hoverOffset: 15,
      },
    ],
  };

  const statusDoughnutData = {
    labels: ["Proses", "Disetujui", "Ditolak"],
    datasets: [
      {
        data: [dataAll.totalStatusProses, dataAll.totalStatusDisetujui, dataAll.totalStatusDitolak],
        backgroundColor: ["#f59e0b", "#22c55e", "#ef4444"],
        borderColor: ["#fff", "#fff", "#fff"],
        borderWidth: 3,
        hoverOffset: 20,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    },
    plugins: {
      legend: {
        position: isMobile ? "bottom" : "top",
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: isMobile ? 12 : 14
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== undefined) {
              label += context.parsed.y.toLocaleString() + ' usulan';
            } else if (context.parsed !== undefined) {
              label += context.parsed.toLocaleString() + ' usulan';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: isMobile ? 11 : 13
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            size: isMobile ? 11 : 13
          },
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      }
    }
  };

  const pieChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        ...chartOptions.plugins.tooltip,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value.toLocaleString()} usulan (${percentage}%)`;
          }
        }
      }
    }
  };

  const doughnutOptions = {
    ...pieChartOptions,
    cutout: '60%',
    plugins: {
      ...pieChartOptions.plugins,
      legend: {
        ...pieChartOptions.plugins.legend,
        position: 'bottom'
      }
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', backgroundColor: '#f8fafc' }}>
   

      <Grid container spacing={3}>
        {/* Statistik Cards */}
        <Grid item xs={12}>
        
        </Grid> 

        {/* Overall Chart */}
        <Grid item xs={12} md={8}>
          <Card 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              height: '100%'
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography 
                               variant="h6" 
                               sx={{ 
                                 mb: 2, 
                                 fontFamily: "'Plus Jakarta Sans', sans-serif",
                                 fontWeight: 600
                               }}
              >
                Grafik Status Usulan Bantuan Rumah
              </Typography>
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={handleChartTypeChange}
                aria-label="chart type"
                size="small"
                sx={{ 
                  backgroundColor: 'grey.100',
                  borderRadius: 2
                }}
              >
                <ToggleButton value="bar" aria-label="bar chart">
                  <BarChartIcon size={18} />
                </ToggleButton>
                <ToggleButton value="pie" aria-label="pie chart">
                  <ChartIcon size={18} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ height: 400 }}>
              {chartType === 'bar' ? (
                <Bar 
                  data={overallBarData} 
                  options={chartOptions}
                />
              ) : (
                <Pie 
                  data={overallPieData} 
                  options={pieChartOptions}
                />
              )}
            </Box>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total: <strong>{dataAll.totalData.toLocaleString()}</strong> • 
                Proses: <strong style={{ color: '#f59e0b' }}>{dataAll.totalStatusProses.toLocaleString()}</strong> • 
                Disetujui: <strong style={{ color: '#22c55e' }}>{dataAll.totalStatusDisetujui.toLocaleString()}</strong> • 
                Ditolak: <strong style={{ color: '#ef4444' }}>{dataAll.totalStatusDitolak.toLocaleString()}</strong>
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Doughnut Chart */}
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              height: '100%'
            }}
          >
            <Typography 
                             variant="h6" 
                             sx={{ 
                               mb: 2, 
                               fontFamily: "'Plus Jakarta Sans', sans-serif",
                               fontWeight: 600
                             }}
            >
            Ringkasan Status
            </Typography>
            
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ height: 300 }}>
              <Doughnut 
                data={statusDoughnutData} 
                options={doughnutOptions}
              />
            </Box>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Ringkasan distribusi status usulan
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Summary Card */}
        {/* <Grid item xs={12}>
          <Card 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white'
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📋 Ringkasan Register Usulan
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total {dataAll.totalData} usulan dengan {dataAll.totalStatusProses} dalam proses, 
                  {dataAll.totalStatusDisetujui} disetujui, dan {dataAll.totalStatusDitolak} ditolak.
                </Typography>
              </Box>
              <TrendingUp size={48} style={{ opacity: 0.8 }} />
            </Stack>
          </Card>
        </Grid> */}
      </Grid>
    </Container>
  );
};

export default GrafikRegisterUsulan;